import React from 'react';
import cx from 'classnames';
import { compact } from 'lodash';
import { Alert, Col, ControlLabel, Row } from 'react-bootstrap';

import FieldInput from '@magoosh/lib/formik_inputs/field_input';
import RadioGroup from '@magoosh/lib/formik_inputs/field_radio_group';
import { toSentence } from 'utilities/formatting';
import { formatPrice } from 'utilities/money';

import BillingZipCode from '@payments/components/inputs/billing_zip_code';
import StripeCardInputs from '@payments/components/inputs/stripe_card';
import { BillingCycle, PaymentMethod } from '@payments/types';
import * as utils from '@payments/utils';

import stripeLogo from './assets/powered_by_stripe.svg';
import style from '@payments/style.module.scss';

interface Props {
  allowedPaymentMethods?: PaymentMethod[];
  billingCycle: BillingCycle;
  onPaymentMethodChanged?: (paymentMethod: string) => void;
  onZipCodeChanged?: (zipCode: string) => void;
  paymentMethod: PaymentMethod;
  paymentRequestMethod?: string;
  totalExcludingTax: number;
}

export const PaymentMethodsFormGroup: React.FC<Props> = ({
  allowedPaymentMethods,
  billingCycle,
  onPaymentMethodChanged,
  onZipCodeChanged,
  paymentMethod,
  paymentRequestMethod,
  totalExcludingTax
}) => {
  // Installments and renewals are billed to a card Stripe keeps on file - typed in, or handed over
  // by a browser wallet - so every other method is off the table.
  const cardOnly = utils.billsRepeatedly(billingCycle);
  const paymentMethods = allowedPaymentMethods || [
    PaymentMethod.STRIPE,
    PaymentMethod.PAYPAL,
    PaymentMethod.ALIPAY,
    PaymentMethod.STRIPE_PAYMENT_REQUEST
  ];

  // The methods that can't bill again are left out rather than greyed out, so the wallet, which
  // only joins the list once the browser says it's available, never lands after a dead option.
  // A note below the list says how to get them back.
  const unavailablePaymentMethodNames = cardOnly ? compact(paymentMethods.map((method) => {
    if (method === PaymentMethod.PAYPAL) {
      return 'PayPal';
    } else if (method === PaymentMethod.ALIPAY) {
      return 'Alipay';
    }
  })) : [];

  const paymentOptions = [
    {
      icons: ['fa fa-credit-card-alt'],
      value: PaymentMethod.STRIPE
    },
    {
      icons: ['fa fa-cc-paypal'],
      value: PaymentMethod.PAYPAL
    },
    {
      icons: ['sprite sprite-alipay subscription-payment-sprite'],
      value: PaymentMethod.ALIPAY
    }
  ].filter((method) => (
    paymentMethods.indexOf(method.value) !== -1 && !(cardOnly && !utils.billsCardOnFile(method.value))
  ));

  // Add Apple/Google pay if available
  if (paymentRequestMethod && paymentMethods.indexOf(PaymentMethod.STRIPE_PAYMENT_REQUEST) !== -1) {
    let iconName = 'sprite';
    if (paymentRequestMethod == 'Google Pay') {
      iconName += ' sprite-google-pay';
    } else if (paymentRequestMethod == 'Apple Pay') {
      iconName += ' sprite-apple-pay';
    }

    paymentOptions.push({
      icons: [iconName],
      value: PaymentMethod.STRIPE_PAYMENT_REQUEST
    });
  }

  return (
    <div className="form-group">
      <fieldset className="inputs">
        <legend className="u-margin-B-n">
          <span>Payment Method</span>
        </legend>
        <Row>
          <Col md={8}>
            {
              paymentMethods.length > 1 && (
                <RadioGroup
                  className="u-margin-T-s"
                  groupClassName="u-margin-A-n"
                  name="paymentMethod"
                  onChangeCondition={(newPaymentMethod: string) => (
                    onPaymentMethodChanged && onPaymentMethodChanged(newPaymentMethod)
                  )}
                  optionsArray={paymentOptions}
                  optionClassName={cx(style.payments, 'radio-inline')}
                  value={cardOnly && !utils.billsCardOnFile(paymentMethod) ? PaymentMethod.STRIPE : paymentMethod}
                />
              )
            }

            {
              unavailablePaymentMethodNames.length > 0 && (
                <div className="help-block u-margin-T-n">
                  <small>
                    {`${toSentence(unavailablePaymentMethodNames, 'and')} ${unavailablePaymentMethodNames.length > 1 ? 'are' : 'is'} available when you select “Pay full ${formatPrice(totalExcludingTax)} today”`}
                  </small>
                </div>
              )
            }

            {
              utils.collectsBillingDetails(paymentMethod) && (
                <>
                  {
                    utils.isCardPayment(paymentMethod) ? (
                      <StripeCardInputs />
                    ) : (
                      // A wallet brings the card, and PayPal and Alipay collect the payment, but
                      // none of them the name and ZIP it is taxed and recorded by. The total they
                      // are asked for has to have the tax already in it.
                      <FieldInput name="cardName" type="input" label="Full Name" />
                    )
                  }

                  <BillingZipCode
                    onZipCodeChanged={(zipCode) => (
                      onZipCodeChanged && onZipCodeChanged(zipCode)
                    )}
                  />
                </>
              )
            }

            {paymentMethod === PaymentMethod.PAYPAL && (
              <Alert bsStyle="secondary">
                Payment will be collected on PayPal after you click &quot;Continue to Payment&quot;
              </Alert>
            )}

            {paymentMethod === PaymentMethod.ALIPAY && (
              <Alert bsStyle="secondary">
                Payment will be collected on Alipay after you click &quot;Continue to Payment&quot;
              </Alert>
            )}
          </Col>
          {
            utils.isCardPayment(paymentMethod) && (
              <Col md={4} className="hidden-sm hidden-xs text-center u-margin-T-s">
                <a href="http://www.stripe.com" target="_blank" rel="noopener noreferrer">
                  <img alt="Powered by Stripe" src={stripeLogo}></img>
                </a>
              </Col>
            )
          }
        </Row>
      </fieldset>
    </div>
  );
};

export default PaymentMethodsFormGroup;
